import { Link } from "react-router-dom";
import { Shield, Check, Phone, Star, MapPin, Bug, Bird, Home, ChevronRight, Clock } from "lucide-react";
import { Logo, Header } from "../components/Layout";
import ServiceAreaMap from "../components/ServiceAreaMap";
import { SERVICE_AREAS_MAP } from "../data/serviceAreas";

const AREA = SERVICE_AREAS_MAP["logan"];

const services = [
  { title: "Pest Control", desc: "Cockroaches, ants, spiders, rodents & more. Same-day service with 6-12 month warranty.", path: "/pests", color: "#E74C3C", icon: Bug },
  { title: "Cockroach Treatment", desc: "Targeted cockroach elimination for German, American & Australian species. All life stages treated.", path: "/pests", color: "#E74C3C", icon: Bug },
  { title: "Termite Inspection", desc: "Thermal imaging & moisture detection. Detailed reports with photos. Catch termites before they cause damage.", path: "/termites", color: "#D2691E", icon: Shield },
  { title: "Termite Treatment", desc: "Chemical barriers & baiting systems to protect your home. 5-8 year protection with warranty.", path: "/termites", color: "#D2691E", icon: Shield },
  { title: "Bird Control", desc: "Solar panel bird proofing, pigeon removal, bird netting & mite treatment. 10-year mesh warranty.", path: "/birds", color: "#5BC0DE", icon: Bird },
  { title: "Solar Bird Proofing", desc: "Stainless steel mesh installed around solar panels. Stop pigeons nesting & damaging your system.", path: "/birds", color: "#5BC0DE", icon: Bird },
  { title: "End of Lease", desc: "Bond-back pest treatment with certificate. Same-day booking available. Accepted by all agents.", path: "/pests", color: "#7ABC32", icon: Home },
  { title: "Affordable Pest Control", desc: "Professional treatments from $165. No hidden fees, no franchise costs. Same quality, lower price.", path: "/pests", color: "#7ABC32", icon: Shield },
];

const suburbs = ["Logan Central", "Woodridge", "Browns Plains", "Marsden", "Crestmead", "Beenleigh", "Eagleby", "Waterford", "Slacks Creek", "Kingston", "Loganholme", "Tanah Merah", "Shailer Park", "Daisy Hill", "Springwood"];

export default function LoganCorridorHubPage() {
  return (
    <>
      <>
        <title>Pest Control Logan QLD | Pest Free Ipswich & Brisbane</title>
        <meta name="description" content="Professional pest control services across the Logan Corridor QLD. Cockroaches, termites, birds, end of lease & more. QBCC licensed. Servicing Logan Central, Woodridge, Browns Plains & surrounds." />
        <link rel="canonical" href="https://pestfreebrisbane.com.au/logan" />
      </>

      <Header />
      <div className="min-h-screen bg-slate-50">
        <section className="bg-gradient-to-br from-slate-900 via-[#1a2332] to-[#7ABC32]/15 text-white pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#7ABC32]/20 border border-[#7ABC32]/30 rounded-full px-4 py-1.5 mb-5">
                  <MapPin className="w-4 h-4 text-[#7ABC32]" />
                  <span className="text-sm font-medium text-[#7ABC32]">Logan QLD</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight" data-testid="logan-hub-h1">
                  Pest Control Services <span className="text-[#7ABC32]">Logan</span>
                </h1>
                <p className="text-lg text-slate-300 mb-6">
                  Fast, affordable pest control across the Logan corridor. From Logan Central and Woodridge to Browns Plains and Beenleigh — we treat every pest problem at competitive prices. High-density housing and subtropical conditions make Logan a pest hotspot. We're here to help.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Same Day Service</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />QBCC Licensed</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Locally Owned</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Fully Insured</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white font-semibold px-6 py-3 rounded-full transition-all" data-testid="logan-call-btn">
                    <Phone className="w-5 h-5" /> (07) 3708 1422
                  </a>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                    <span className="text-sm text-slate-300 ml-1">4.9 on Google</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Logo className="h-32 md:h-40 opacity-90" />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">Our Logan Services</h2>
              <p className="text-slate-600 mt-2 max-w-2xl mx-auto">Choose a service below to learn more and get an instant quote</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="services-grid">
              {services.map((s, i) => (
                <Link key={i} to={s.path} className="group bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:border-slate-300 transition-all">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: s.color + '12' }}>
                    <s.icon className="w-5 h-5" style={{ color: s.color }} />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2 group-hover:text-[#7ABC32] transition-colors">{s.title}</h3>
                  <p className="text-sm text-slate-600 mb-3">{s.desc}</p>
                  <span className="inline-flex items-center text-sm font-semibold text-[#7ABC32] group-hover:gap-2 transition-all">
                    Get Quote <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Why Logan Chooses Pest Free</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Logan Locals", desc: "We service the Logan corridor frequently. We understand the local pest pressures from high-density living, subtropical climate, and proximity to bushland.", color: "#7ABC32" },
                { title: "Affordable Rates", desc: "No franchise fees means lower prices for you. Professional pest control from $165 — same quality products as the big companies at a fraction of the cost.", color: "#5BC0DE" },
                { title: "Fast Response", desc: "Same-day and next-day service across all Logan suburbs. We prioritise end-of-lease deadlines and urgent pest problems.", color: "#E74C3C" },
              ].map((item, i) => (
                <div key={i} className="text-center p-6 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: item.color + '12' }}>
                    <Check className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">Our Logan Service Area</h2>
            <p className="text-slate-600 mb-8 max-w-2xl mx-auto">The shaded green area below shows the suburbs we regularly service across the Logan corridor.</p>
            <ServiceAreaMap
              areaKey="logan"
              center={AREA.center}
              zoom={AREA.zoom}
              polygon={AREA.polygon}
              suburbs={AREA.suburbs}
            />
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Serving All Logan Suburbs</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {suburbs.map(s => (
                <span key={s} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">{s}</span>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-gradient-to-r from-slate-900 to-[#7ABC32] text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Logan's Local Pest Experts</h2>
            <p className="text-lg text-white/80 mb-6">Choose a service above for an instant quote, or call us now.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold px-8 py-3 rounded-full hover:bg-slate-100 transition-all">
                <Phone className="w-5 h-5" /> Call (07) 3708 1422
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
